import { Layout, Text, TextLink } from '@oneaudi/unified-web-components';
import { useAtomValue } from 'jotai';
import { FsagMonthlyPriceRateDialog } from '~/app/compositions/content/calculation-info/components/fsag-finance-dialog';
import MonthlyRateFootnote from '~/app/compositions/content/calculation-info/components/monthly-rate-footnote';
import {
	CalculationInfoStyle,
	MonthlyPrice,
	PriceBarTotalPrice,
	TotalPrice,
} from '~/app/compositions/content/calculation-info/styles/calculation-info-style';
import { useAppServices } from '~/app/contexts/app-context';
import { useDialogs } from '~/app/hooks/use-dialogs';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { PriceDetailsAsBuiltDialog } from '~/app/pages/summary/dialogs/price-detail';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import { useCurrencyFormatter } from '~/hooks/use-currency-formatter';
import { BasicProps } from '~/models/basic-props';
import { state } from '~/storage';
import { DATA_TEST_ID } from '~/test/data-test-id';

export function CalculationInfoAsBuild({ className }: BasicProps) {
	const { config } = useMarketConfig();
	const { loadingFlags, catalog } = useConfigurator();
	const { formatMonthlyRate, formatPriceWithVAT } = useCurrencyFormatter();
	const showLoadingIndicator = loadingFlags.priceSummary;
	const currentView = useAtomValue(state.currentView);
	const { i18n } = useI18n();
	const totalPrice = catalog.priceSummary?.totalPrice;
	const monthlyRate = catalog.priceSummary?.monthlyRate;
	const {
		featureServices: { footnoteService },
	} = useAppServices();
	footnoteService?.getData();

	const { openDialog, isDialogOpen, closeDialog } = useDialogs();

	function scrollToBottom() {
		window?.scrollTo({ left: 0, top: document?.body.scrollHeight, behavior: 'smooth' });
	}

	return (
		<CalculationInfoStyle $view={currentView} className={className}>
			{showLoadingIndicator ? (
				<Text
					tag={'p'}
					variant={{ type: 'copy', order: '1', style: 'bold' }}
					className="calculation-info-item loading">
					{i18n('ngc.currency.calculatingPrice')}...
				</Text>
			) : (
				<>
					<div className={'calculation-info total-price'} data-testid={DATA_TEST_ID.TOTAL_PRICE_CONTAINER}>
						<PriceBarTotalPrice>
							<Text
								tag={'p'}
								variant={{ type: 'copy', order: '2', style: 'normal' }}
								className="calculation-info-item">
								{i18n('ngc.priceDetails.priceAsBuilt')}
								<span style={{ cursor: 'pointer', marginLeft: '2px' }} onClick={scrollToBottom}>
									{' '}
									*{' '}
								</span>
							</Text>
							<Layout>
								<TotalPrice>{formatPriceWithVAT(totalPrice)}</TotalPrice>
							</Layout>
						</PriceBarTotalPrice>

						<TextLink
							className="text-grey"
							onClick={() => openDialog('priceDetails')}
							size={'sm'}
							variant={'standalone'}>
							{i18n('ngc.actions.showPriceDetails')}
						</TextLink>

						<PriceDetailsAsBuiltDialog
							open={isDialogOpen('priceDetails')}
							onClose={() => closeDialog('priceDetails')}
						/>
					</div>
					{monthlyRate && monthlyRate.__typename === 'MonthlyRate' && config.enableFinancingServices && (
						<div className="calculation-info monthly-price">
							<MonthlyPrice>
								{formatMonthlyRate(monthlyRate.rawValue)}
								<MonthlyRateFootnote text="Horst :)" />
							</MonthlyPrice>
							<TextLink
								className="text-grey"
								onClick={() => openDialog('monthlyPriceRatesFsag')}
								size={'sm'}
								variant={'standalone'}>
								{i18n('ngc.actions.changeLeasingRate')}
							</TextLink>
						</div>
					)}
					{monthlyRate && config.enableFinancingServices && (
						<FsagMonthlyPriceRateDialog
							open={isDialogOpen('monthlyPriceRatesFsag')}
							onClose={() => closeDialog('monthlyPriceRatesFsag')}
						/>
					)}
				</>
			)}
		</CalculationInfoStyle>
	);
}
